import React, { useEffect, useState } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Button, CardContent, CardActions, Card, TextField, Box, Stack, Switch, FormControl, Checkbox, FormControlLabel, FormHelperText, MenuItem, Select, InputLabel, Autocomplete, Grid, Divider, Typography, IconButton, Modal, Chip, LinearProgress, InputAdornment } from '@mui/material';
import { Post, apiURL, Get, Put } from '../../../utils/apiCalls';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Base64File } from '../../../utils/Base64File';
import { styled } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import { dataURItoBlob } from '../../../utils/Base64File';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import KYCService, { KYC_STATUS } from '../../../utils/kycService';
import { handleKYCError, handleKYCSuccess } from '../../../utils/kycUtils';
import useUserDetails from '../../../hooks/useUserDetails';
import OtpInput from 'react-otp-input';

const ProcurementAdminSchema = Yup.object().shape({
   first_name: Yup.string().required('First Name is required'),
   last_name: Yup.string().required('Last Name is required'),
   email: Yup.string().email('Invalid Email').required('Email is required'),
   password: Yup.string().required('M-Pin is required').matches(/^[0-9]{4}$/, 'Invalid M-Pin'),
   phone: Yup.string().matches(/^[6-9]\d{9}$/, 'Please Enter valid Phone Number').required('Phone Number is required'),
   aadhar_number: Yup.string().matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhar number').required('Aadhar Number is required'),
   pan_number: Yup.string().required('Pan Number is required'),
   address: Yup.string().required('Personal Address is required'),
   city: Yup.string().required('City is required'),
   state: Yup.string().required('State is required'),
   country: Yup.string().required('Country is required'),
   pincode: Yup.string().required('Pincode is required'),
   role: Yup.string().required('Role is required'),
   active: Yup.string().required('Active is required'),
   check_director: Yup.boolean(),
   ref_agent_id: Yup.string()
      .when("check_director", (check_director, schema) => {
         if (!check_director[0]) {
            return schema.required("Associate is required")
         }
         return schema
      }),
   bank_name: Yup.string().required('Bank Name is required'),
   bank_account_number: Yup.string().required('Account Number is required'),
   bank_ifsc_code: Yup.string().required('IFSC Number is required'),
   bank_address: Yup.string().required('Bank Address is required'),
});

const ProcurementAdminSchemaEdit = Yup.object().shape({
   first_name: Yup.string().required('First Name is required'),
   last_name: Yup.string().required('Last Name is required'),
   email: Yup.string().email('Invalid Email').required('Email is required'),
   phone: Yup.string().matches(/^[6-9]\d{9}$/, 'Please Enter valid Phone Number').required('Phone Number is required'),
   aadhar_number: Yup.string().matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhar number').required('Aadhar Number is required'),
   pan_number: Yup.string().required('Pan Number is required'),
   address: Yup.string().required('Personal Address is required'),
   city: Yup.string().required('City is required'),
   state: Yup.string().required('State is required'),
   country: Yup.string().required('Country is required'),
   pincode: Yup.string().required('Pincode is required'),
   role: Yup.string().required('Role is required'),
   active: Yup.string().required('Active is required'),
   check_director: Yup.boolean(),
   ref_agent_id: Yup.string()
      .when("check_director", (check_director, schema) => {
         if (!check_director[0]) {
            return schema.required("Associate is required")
         }
         return schema
      }),
   bank_name: Yup.string().required('Bank Name is required'),
   bank_account_number: Yup.string().required('Account Number is required'),
   bank_ifsc_code: Yup.string().required('IFSC Number is required'),
   bank_address: Yup.string().required('Bank Address is required'),
   // KYC Fields
   pan_date_of_birth: Yup.date(),
   pan_name: Yup.string(),
   aadhaar_name: Yup.string(),
   bank_account_holder_name: Yup.string(),
});

const AdminProcurementCreate = () => {
   const { id } = useParams()
   const navigate = useNavigate();
   const userDetails = useUserDetails();
   const [roles, setRoles] = useState([]);
   const [procurementUsers, setProcurementUsers] = useState([]);
   const [checked, setChecked] = useState(false);
   const [upload, setUpload] = useState({ aadhar_upload: '', pan_upload: '' });
   const [kycStatus, setKycStatus] = useState({
      pan: null,
      aadhaar: null,
      bank: null
   });
   const [kycLoading, setKycLoading] = useState({
      pan: false,
      aadhaar: false,
      bank: false
   });
   const [verificationModalOpen, setVerificationModalOpen] = useState(false);
   const [verificationType, setVerificationType] = useState('');
   const [otpModalOpen, setOtpModalOpen] = useState(false);
   const [otpType, setOtpType] = useState('');
   const [otpValue, setOtpValue] = useState('');
   const [isEmailVerified, setIsEmailVerified] = useState(false);
   const [isPhoneVerified, setIsPhoneVerified] = useState(false);

   const [data, setData] = useState({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      aadhar_number: '',
      pan_number: '',
      aadhar_upload: '',
      pan_upload: '',
      address: '',
      bank_name: '',
      bank_account_number: '',
      bank_ifsc_code: '',
      bank_address: '',
      role: '',
      ref_agent_id: '',
      check_director: false,
      city: '',
      state: '',
      country: '',
      pincode: '',
      nominee_relation: '',
      nominee_age: '',
      nominee_name: '',
      active: 'yes',
      pan_date_of_birth: '',
      pan_name: '',
      aadhaar_name: '',
      bank_account_holder_name: '',
      pan_verified: false,
      aadhaar_verified: false,
      bank_verified: false,
      is_kyc:true
   });

   // const getRolesbyID = async (roleCode) => {
   //    const response = await Get(apiURL + '/api/v1/procurement-admin-roles','')
   //    if (response && response.data && response.data.status) {
   //       // Filter roles based on level_code hierarchy
   //       const allRoles = response.data.data;
   //       if (roleCode) {
   //          const selectedRole = allRoles.find(r => r.level_code === roleCode);
   //          if (selectedRole) {
   //             // Get roles that are below the selected role in hierarchy
   //             const filteredRoles = allRoles.filter(r => r.level_code > selectedRole.level_code);
   //             setRoles(filteredRoles);
   //             return filteredRoles;
   //          } else {
   //             setRoles(allRoles);
   //             return allRoles;
   //          }
   //       } else {
   //          // If no roleCode, return all roles
   //          setRoles(allRoles);
   //          return allRoles;
   //       }
   //    }
   //    return [];
   // }

const getRolesbyID = async (id) => {
   console.log(id,"id");
   const parsedId = parseInt(id);
   const queryParam = !isNaN(parsedId) ? `?level_code=${parsedId}` : '';
   const response = await Get(apiURL + '/api/v1/procurement-admin-roles' + queryParam, '')
   if (response && response.data && response.data.status) {
      setRoles(response.data.data)
   }
}
   
   const Roles = async () => {
      if (id) {
         const responseProcurementUsers = await Get(apiURL + '/api/v1/procurement-admin-users','')
         const responseGet = await Get(apiURL + `/api/v1/procurement-admin-users/${id}`,'')
         if (responseGet && responseGet.data && responseGet.data.status) {
            const userData = responseGet.data.data;
            const bankDetails = userData.bank_details && userData.bank_details.length > 0 ? userData.bank_details[0] : {};
            
            // Extract role level_code - it might be a string or an object
            let roleCode = '';
            if (typeof userData.role === 'string') {
               roleCode = userData.role;
            } else if (userData.role && userData.role.level_code) {
               roleCode = userData.role.level_code;
            } else if (userData.role && typeof userData.role === 'object') {
               roleCode = userData.role.level_code || '';
            }
            
            // Set data similar to AdminAgentCreate
            const formData = {
               ...userData,
               bank_name: bankDetails.bank_name || '',
               bank_account_number: bankDetails.bank_account_number || '',
               bank_ifsc_code: bankDetails.bank_ifsc_code || '',
               bank_address: bankDetails.bank_address || '',
               role: roleCode,
               ref_agent_id: userData.ref_agent_id || '',
               check_director: roleCode === '1' || roleCode === 1 || false,
               pan_date_of_birth: userData.pan_date_of_birth || '',
               pan_name: userData.pan_name || '',
               aadhaar_name: userData.aadhaar_name || '',
               bank_account_holder_name: bankDetails.name || bankDetails.account_holder_name || '',
               pan_verified: userData.pan_verified || false,
               aadhaar_verified: userData.aadhaar_verified || false,
               bank_verified: userData.bank_verified || false
            };
            setData(formData);
            
            // Set email and phone as verified in edit mode
            setIsEmailVerified(true);
            setIsPhoneVerified(true);
            
            // Handle documents - similar to AdminAgentCreate
            if (userData.docs && userData.docs.length > 0) {
               const docMap = {};
               userData.docs.forEach(doc => {
                  if (doc.document_type === 'Aadhaar Card' || doc.document_type === 'AAdhar Card' || doc.document_type === 'aadhar_upload') {
                     docMap.aadhar_upload = doc;
                  } else if (doc.document_type === 'PAN Card' || doc.document_type === 'Pan Card' || doc.document_type === 'pan_upload') {
                     docMap.pan_upload = doc;
                  }
               });
               setUpload(docMap);
            } else if (userData.doc_id) {
               // Fallback to doc_id structure if available
               setUpload({ ...userData.doc_id });
            }

            if (userData.ref_agent_id) {
               if (responseProcurementUsers && responseProcurementUsers.data && responseProcurementUsers.data.status) {
                  const refUser = responseProcurementUsers.data.data.find((data) => data.id === userData.ref_agent_id);
                  if (refUser) {
                     // Get role code from ref user
                     let refRoleCode = '';
                     if (typeof refUser.role === 'string') {
                        refRoleCode = refUser.role;
                     } else if (refUser.role && refUser.role.level_code) {
                        refRoleCode = refUser.role.level_code;
                     }
                     if (refRoleCode) {
                        await getRolesbyID(refRoleCode);
                     }
                  }
                  setProcurementUsers(responseProcurementUsers.data.data);
               }
            }
         }
         else {
            toast.error('Please Try Again')
            navigate('/dashboard/adminProcurementUser')
         }
      }

      // Get all procurement admin users for dropdown (for create mode)
      const responseProcurementUsers = await Get(apiURL + '/api/v1/procurement-admin-users','')
      if (responseProcurementUsers && responseProcurementUsers.data && responseProcurementUsers.data.status) {
         setProcurementUsers(responseProcurementUsers.data.data)
      }

      // Get all procurement admin roles (for create mode)
      if (!id) {
         await getRolesbyID(null);
      }
   }

   useEffect(() => {
      Roles()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   const formik = useFormik({
      initialValues: {
         ...data
      },
      enableReinitialize: true,
      validationSchema: id ? ProcurementAdminSchemaEdit : ProcurementAdminSchema,
      onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
         console.log(values,"values");
         // For create mode, check OTP and KYC verifications
         if (!id) {
            if (!isEmailVerified && !isPhoneVerified) {
               setFieldError('email', 'Please verify Email OTP');
               setFieldError('phone', 'Please verify Phone OTP');
               setFieldTouched('email', true);
               setFieldTouched('phone', true);
               setSubmitting(false);
               return;
            } else if (!isEmailVerified) {
               setFieldError('email', 'Please verify Email OTP');
               setFieldTouched('email', true);
               setSubmitting(false);
               return;
            } else if (!isPhoneVerified) {
               setFieldError('phone', 'Please verify Phone OTP');
               setFieldTouched('phone', true);
               setSubmitting(false);
               return;
            }
            
            if (!values.pan_verified || !values.aadhaar_verified || !values.bank_verified) {
               toast.error('Please complete all KYC verifications before submitting');
               setSubmitting(false);
               return;
            }
         }

         if (id) {
            // Edit mode - update existing procurement admin user (same pattern as AdminAgentCreate)
            const updateValues = { ...values };
            if (!updateValues.password || updateValues.password.trim() === '') {
               delete updateValues.password;
            }
            
            const response = await Put(apiURL + `/api/v1/procurement-admin-users/${id}`, updateValues);
            if (response && response.data && response.data.status) {
               toast.success(response.data.message)
               setSubmitting(false)
               navigate('/dashboard/adminProcurementUser')
            }
            else {
               toast.error(response?.data?.message || 'Please Try Again')
               setSubmitting(false)
            }
         }
         else {
            // Create mode
            const createValues = { 
               ...values,
               // Bank details will be created automatically by the backend if these fields are present
            };
            const response = await Post(apiURL + '/api/v1/procurement-admin-users', createValues);
            if (response && response.data && response.data.status) {
               toast.success(response.data.message)
               setSubmitting(false)
               navigate('/dashboard/adminProcurementUser')
            }
            else {
               toast.error(response?.data?.message || 'Please Try Again')
               setSubmitting(false)
            }
         }
      }
   });

   const { errors, touched, values, setFieldValue, setFieldError, isSubmitting, handleSubmit, getFieldProps, handleBlur } = formik;

   const handleDocUpload = async (id, file) => {
      const data = { file: id === 'aadhar_upload' ? 'Aadhar Card' : 'Pan Card', data: file }
      const response = await Post(apiURL + '/api/v1/users/uploaddoc', { ...data }, navigate)
      if (response && response.data && response.data.status) {
         setUpload((prev) => ({ ...prev, [id]: response?.data?.data?.data }))
         toast.success(`${data.file} Uploaded Successfully`)
         setFieldValue(id, response?.data?.data?.id)
      }
      else {
         toast.error(response?.data?.message || 'Please Try Again')
      }
   }

   const handleFiles = async (e) => {
      const { id, files } = e.target
      if ((files[0]['size'] / 1024) <= 2000) {
         const base64 = await Base64File(files[0])
         const file = { name: files[0]['name'], size: files[0]['size'], base64: base64 }
         handleDocUpload(id, file)
      }
      else {
         toast.error('Please Upload File less than 2 MB')
      }
   }

   const handleChangeCheck = (e) => {
      setFieldValue('check_director', e.target.checked)
      if (e.target.checked) {
         setFieldValue('role', '1')
      } else {
         setFieldValue('role', '')
      }
   }

   const handleChangeCheckTerms = (e) => {
      setChecked(e.target.checked)
   }

   // OTP Verification Functions
   const handleOtpOpen = async (type) => {
      const response = await Post(apiURL + `/api/v1/users/registration/_sent_otp_registration`, { entity: type === 'email' ? values.email : values.phone });
      if (response && response.data && response.data.status) {
         toast.success('OTP sent successfully');
         setOtpType(type);
         setOtpModalOpen(true);
      } else {
         toast.error('Failed to send OTP. Please try again.');
      }
   };

   const handleOtpClose = () => {
      setOtpModalOpen(false);
      setOtpValue('');
   };

   const handleOtpSubmit = async () => {
      const response = await Post(apiURL + `/api/v1/users/registration/_verify_otp_registration`, { entity: otpType === 'email' ? values.email : values.phone, otp: otpValue });
      if (response && response.data && response.data.status) {
         toast.success('OTP Verified Successfully');
         setOtpModalOpen(false);
         setOtpValue('');
         if (otpType === 'email') {
            setIsEmailVerified(true);
            setFieldError('email', '');
         } else {
            setIsPhoneVerified(true);
            setFieldError('phone', '');
         }
      } else {
         toast.error('Invalid OTP. Please try again.');
      }
   };

   // KYC Verification Functions
   const handlePANVerification = async () => {
      try {
         setKycLoading(prev => ({ ...prev, pan: true }));
         setVerificationType('PAN');
         setVerificationModalOpen(true);
         
         const panData = {
            pan_number: values.pan_number,
            date_of_birth: values.pan_date_of_birth,
            full_name: values.first_name + ' ' + values.last_name
         };

         const userId = id || userDetails?.id || (() => {
            try {
               const storedUser = sessionStorage.getItem('user_details');
               if (storedUser) {
                  const parsedUser = JSON.parse(storedUser);
                  return parsedUser?.id;
               }
            } catch (error) {
               console.error('Error reading from sessionStorage:', error);
            }
            return null;
         })();

         const response = await KYCService.verifyPAN(panData, userId);
         
         if (response?.data?.status) {
            handleKYCSuccess('PAN verification submitted successfully');
            setFieldValue('pan_verified', true);
            setKycStatus(prev => ({ ...prev, pan: KYC_STATUS.PROCESSING }));
         }
      } catch (error) {
         handleKYCError(error, 'PAN verification');
      } finally {
         setKycLoading(prev => ({ ...prev, pan: false }));
         setVerificationModalOpen(false);
      }
   };

   const handleAadhaarVerification = async () => {
      try {
         setKycLoading(prev => ({ ...prev, aadhaar: true }));
         setVerificationType('Aadhaar');
         setVerificationModalOpen(true);
         
         const aadhaarData = {
            aadhaar_number: values.aadhar_number,
            date_of_birth: values.pan_date_of_birth,
         };

         const userId = id || userDetails?.id || (() => {
            try {
               const storedUser = sessionStorage.getItem('user_details');
               if (storedUser) {
                  const parsedUser = JSON.parse(storedUser);
                  return parsedUser?.id;
               }
            } catch (error) {
               console.error('Error reading from sessionStorage:', error);
            }
            return null;
         })();

         const response = await KYCService.verifyAadhaar(aadhaarData, userId);
         
         if (response?.data?.status) {
            handleKYCSuccess('Aadhaar verification submitted successfully');
            setFieldValue('aadhaar_verified', true);
            setKycStatus(prev => ({ ...prev, aadhaar: KYC_STATUS.PROCESSING }));
         }
      } catch (error) {
         handleKYCError(error, 'Aadhaar verification');
      } finally {
         setKycLoading(prev => ({ ...prev, aadhaar: false }));
         setVerificationModalOpen(false);
      }
   };

   const handleBankVerification = async () => {
      try {
         setKycLoading(prev => ({ ...prev, bank: true }));
         setVerificationType('Bank Account');
         setVerificationModalOpen(true);
         
         const bankData = {
            account_number: values.bank_account_number,
            ifsc_code: values.bank_ifsc_code,
            account_holder_name: values.bank_account_holder_name
         };

         const userId = userDetails?.id || (() => {
            try {
               const storedUser = sessionStorage.getItem('user_details');
               if (storedUser) {
                  const parsedUser = JSON.parse(storedUser);
                  return parsedUser?.id;
               }
            } catch (error) {
               console.error('Error reading from sessionStorage:', error);
            }
            return null;
         })();

         const response = await KYCService.verifyBank(bankData, userId);
         
         if (response?.data?.status) {
            handleKYCSuccess('Bank verification submitted successfully');
            setFieldValue('bank_verified', true);
            setKycStatus(prev => ({ ...prev, bank: KYC_STATUS.PROCESSING }));
         }
      } catch (error) {
         handleKYCError(error, 'Bank verification');
      } finally {
         setKycLoading(prev => ({ ...prev, bank: false }));
         setVerificationModalOpen(false);
      }
   };

   const getStatusIcon = (status) => {
      switch (status) {
         case KYC_STATUS.VERIFIED:
            return <VerifiedUserIcon color="success" />;
         case KYC_STATUS.PROCESSING:
            return <PendingIcon color="warning" />;
         case KYC_STATUS.REJECTED:
            return <ErrorIcon color="error" />;
         default:
            return <PendingIcon color="disabled" />;
      }
   };

   return (
      <FormikProvider value={formik}>
         <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Card>
               <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                     <Box
                        sx={{
                           bgcolor: "#1976d2",
                           borderRadius: "50%",
                           width: 64,
                           height: 64,
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                        }}
                     >
                        <BusinessIcon sx={{ color: "#fff", fontSize: 32 }} />
                     </Box>
                     <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                           {id ? 'Edit Procurement User' : 'Create Procurement User'}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                           {id ? 'Update procurement user details' : 'Create a new procurement user'}
                        </Typography>
                     </Box>
                  </Stack>
                  
                  <Divider sx={{ mb: 3 }} />

                  {/* Progress Indicator - Only for create mode */}
                  {!id && (
                     <Box sx={{ mb: 4 }}>
                        <LinearProgress 
                           variant="determinate" 
                           value={((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0)) * 33.33}
                           sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                 borderRadius: 4,
                              }
                           }} 
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                           KYC Progress: {((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0))}/3 completed
                        </Typography>
                     </Box>
                  )}

                  {/* Director/Associate Section */}
                  <Box sx={{ mb: 4 }}>
                     {id && values.ref_agent_id ? (
                        <Grid container spacing={2}>
                           <Grid item xs={12} sm={12} lg={6} md={6} xl={6}>
                              <Autocomplete fullWidth disableClearable
                                 name='ref_agent_id'
                                 id='ref_agent_id'
                                 value={values.ref_agent_id ? procurementUsers.find((data) => data.id === values.ref_agent_id) : {}}
                                 options={procurementUsers}
                                 getOptionLabel={(option) =>
                                    option ? `${option.first_name} ${option.last_name}` : ''
                                 }
                                 renderOption={(props, option) => (
                                    <Box component='li' sx={{ minWidth: '100px' }} {...props}>
                                       {option.first_name}&nbsp;{option.last_name}
                                    </Box>
                                 )}
                                 renderInput={(params) => (
                                    <TextField
                                       error={touched.ref_agent_id && Boolean(errors.ref_agent_id)}
                                       helperText={touched.ref_agent_id && errors.ref_agent_id}
                                       {...params}
                                       label='Associate'
                                    />
                                 )}
                                 onChange={async (e, newvalue, reason) => {
                                    let roleCode = '';
                                    if (newvalue) {
                                       if (typeof newvalue.role === 'string') {
                                          roleCode = newvalue.role;
                                       } else if (newvalue.role && newvalue.role.level_code) {
                                          roleCode = newvalue.role.level_code;
                                       }
                                       if (roleCode) {
                                          // Filter roles based on selected user's role level
                                          const filteredRoles = await getRolesbyID(roleCode);
                                          // Clear role field only in create mode if current role is not in filtered list
                                          if (!id && values.role && filteredRoles.length > 0) {
                                             const currentRoleValid = filteredRoles.some(r => r.level_code === values.role);
                                             if (!currentRoleValid) {
                                                setFieldValue('role', '');
                                             }
                                          } else if (!id && filteredRoles.length === 0) {
                                             setFieldValue('role', '');
                                          }
                                       }
                                    } else {
                                       // If no user selected, reset role and load all roles (only in create mode)
                                       if (!id) {
                                          setFieldValue('role', '');
                                       }
                                       await getRolesbyID(null);
                                    }
                                    setFieldValue('ref_agent_id', newvalue ? newvalue?.['id'] : '')
                                 }}
                              />
                           </Grid>
                        </Grid>
                     ) : !id ? (
                        <Grid container spacing={2}>
                           <Grid item xs={12} sm={12} lg={4} md={6} xl={4}>
                              <FormControl component="fieldset" variant="standard" fullWidth>
                                 <FormControlLabel
                                    control={
                                       <Switch checked={values.check_director} onChange={handleChangeCheck} name="director" />
                                    }
                                    label="Switch On if Procurement Director"
                                 />
                              </FormControl>
                           </Grid>
                           <Grid item xs={12} sm={12} lg={4} md={6} xl={4}>
                              {!values.check_director ? (
                                 <Autocomplete fullWidth
                                    name='ref_agent_id'
                                    id='ref_agent_id'
                                    value={values.ref_agent_id ? procurementUsers.find((data) => data.id === values.ref_agent_id) : ''}
                                    options={procurementUsers}
                                    getOptionLabel={(option) =>
                                       option ? `${option.first_name} ${option.last_name}` : ''
                                    }
                                    renderOption={(props, option) => (
                                       <Box component='li' sx={{ minWidth: '100px' }} {...props}>
                                          {option.first_name}&nbsp;{option.last_name}
                                       </Box>
                                    )}
                                    renderInput={(params) => (
                                       <TextField
                                          error={touched.ref_agent_id && Boolean(errors.ref_agent_id)}
                                          helperText={touched.ref_agent_id && errors.ref_agent_id}
                                          {...params}
                                          label='Procurement User'
                                       />
                                    )}
                                    onChange={async (e, newvalue, reason) => {
                                       let roleCode = '';
                                       if (newvalue) {
                                          if (typeof newvalue.role === 'string') {
                                             roleCode = newvalue.role;
                                          } else if (newvalue.role && newvalue.role.level_code) {
                                             roleCode = newvalue.role.level_code;
                                          }
                                       }
                                       getRolesbyID(roleCode || null);
                                       setFieldValue('ref_agent_id', newvalue ? newvalue?.['id'] : '')
                                    }}
                                 />
                              ) : null}
                           </Grid>
                        </Grid>
                     ) : null}
                  </Box>

                  {/* Role Section */}
                  <Box sx={{ mb: 4 }}>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           {!values.check_director ? (
                              <Autocomplete fullWidth
                                 name='role'
                                 id='role'
                                 value={values.role ? roles.find((data) => String(data.level_code) === String(values.role)) : ''}
                                 options={roles}
                                 getOptionLabel={(option) =>
                                    option.designation ?? option
                                 }
                                 renderOption={(props, option) => (
                                    <Box component='li' sx={{ minWidth: '100px' }} {...props}>
                                       {option.designation}
                                    </Box>
                                 )}
                                 renderInput={(params) => (
                                    <TextField
                                       error={touched.role && Boolean(errors.role)}
                                       helperText={touched.role && errors.role}
                                       {...params}
                                       label='Position or Role'
                                    />
                                 )}
                                 onChange={(e, newvalue, reason) => {
                                    setFieldValue('role', newvalue ? newvalue?.['level_code'] : '')
                                 }}
                              />
                           ) : (
                              <TextField disabled value="Procurement Director" label='Position or Role' fullWidth />
                           )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <FormControl fullWidth error={Boolean(touched.active && errors.active)}>
                              <InputLabel id="active">Active</InputLabel>
                              <Select
                                 id="active"
                                 value={values.active}
                                 label="Active"
                                 onChange={(e) => setFieldValue('active', e.target.value)}
                              >
                                 <MenuItem value={'yes'}>Yes</MenuItem>
                                 <MenuItem value={'no'}>No</MenuItem>
                              </Select>
                              <FormHelperText>{touched.active && errors.active}</FormHelperText>
                           </FormControl>
                        </Grid>
                     </Grid>
                  </Box>
{console.log(errors,"errors")}
                  {/* Section 1: Personal Information */}
                  <Box sx={{ mb: 4 }}>
                     <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        1. Personal Information
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="First Name" {...getFieldProps('first_name')} error={Boolean(touched.first_name && errors.first_name)} helperText={touched.first_name && errors.first_name} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="Last Name" {...getFieldProps('last_name')} error={Boolean(touched.last_name && errors.last_name)} helperText={touched.last_name && errors.last_name} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Email Address"
                              type="email"
                              {...getFieldProps('email')}
                              error={Boolean(touched.email && errors.email)}
                              helperText={touched.email && errors.email}
                              disabled={!!id}
                              InputProps={{
                                 endAdornment: !id && (
                                    <InputAdornment position="end">
                                       {isEmailVerified ? (
                                          <CheckCircleIcon color="success" />
                                       ) : (
                                          <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() => handleOtpOpen("email")}
                                          >
                                             Verify
                                          </Button>
                                       )}
                                    </InputAdornment>
                                 ),
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {id ? null : (
                               <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                     M-Pin
                                  </Typography>
                                  <OtpInput
                                     numInputs={4}
                                     value={values.password}
                                     onChange={(val) => setFieldValue('password', val)}
                                     containerStyle={{ gap: '15px', display: 'flex' }}
                                     inputStyle={{
                                        color: '#001B71',
                                        WebkitTextFillColor: 'black !important',
                                        padding: '0px',
                                        fontWeight: 600,
                                        fontSize: '1.25rem',
                                        borderRadius: '4px',
                                        boxSizing: 'content-box',
                                        height: '50px',
                                        display: 'block',
                                        width: '45px',
                                        minWidth: '0px',
                                        border: '1px solid #c4c4c4'
                                     }}
                                     renderInput={(props) => <input {...props} type="password" />}
                                  />
                                  {touched.password && errors.password && (
                                     <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                        {errors.password}
                                     </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                     Enter 4 digit M-Pin
                                  </Typography>
                               </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Phone Number"
                              {...getFieldProps('phone')}
                              error={Boolean(touched.phone && errors.phone)}
                              helperText={touched.phone && errors.phone}
                              disabled={!!id}
                              InputProps={{
                                 endAdornment: !id && (
                                    <InputAdornment position="end">
                                       {isPhoneVerified ? (
                                          <CheckCircleIcon color="success" />
                                       ) : (
                                          <Button
                                             variant="outlined"
                                             size="small"
                                             onClick={() => handleOtpOpen("phone")}
                                          >
                                             Verify
                                          </Button>
                                       )}
                                    </InputAdornment>
                                 ),
                              }}
                           />
                        </Grid>
                     </Grid>
                  </Box>

                  {/* Section 2: PAN Card Verification */}
                  <Box sx={{ mb: 4 }}>
                     <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        2. PAN Card Verification
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="PAN Number"
                              {...getFieldProps('pan_number')}
                              error={Boolean(touched.pan_number && errors.pan_number)}
                              helperText={touched.pan_number && errors.pan_number}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              disabled={values.pan_verified && !!id}
                              InputProps={{
                                 endAdornment: values.pan_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              type="date"
                              label="Date of Birth"
                              InputLabelProps={{ shrink: true }}
                              {...getFieldProps('pan_date_of_birth')}
                              error={Boolean(touched.pan_date_of_birth && errors.pan_date_of_birth)}
                              helperText={touched.pan_date_of_birth && errors.pan_date_of_birth}
                              disabled={values.pan_verified && !!id}
                              InputProps={{
                                 endAdornment: values.pan_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <Stack spacing={2}>
                              <Button
                                 component="label"
                                 variant="contained"
                                 startIcon={<CloudUploadIcon />}
                                 disabled={values.pan_verified && !!id}
                              >
                                 Upload PAN Card
                                 <VisuallyHiddenInput id='pan_upload' type="file" onChange={(e) => handleFiles(e)} />
                              </Button>
                              <Stack direction={'row'} spacing={1}>
                                 <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                                    {upload.pan_upload?.['name'] ? upload.pan_upload?.['name'] : "No File Chosen"}
                                 </Typography>
                                 {upload.pan_upload?.['name'] ? (
                                    <IconButton component={'a'} href={dataURItoBlob(upload.pan_upload?.['base64'])} target='_blank' rel="noreferrer">
                                       <LaunchIcon />
                                    </IconButton>
                                 ) : null}
                              </Stack>
                           </Stack>
                        </Grid>
                     </Grid>
                     
                     {!id && (
                        <Box sx={{ mt: 3 }}>
                           <LoadingButton
                              variant="contained"
                              onClick={handlePANVerification}
                              loading={kycLoading.pan}
                              disabled={(!values.pan_number || !values.pan_date_of_birth || !upload.pan_upload?.name) || values.pan_verified}
                              startIcon={getStatusIcon(kycStatus.pan)}
                              sx={{ mr: 2 }}
                           >
                              {kycLoading.pan ? 'Verifying...' : values.pan_verified ? 'Verified' : 'Verify PAN'}
                           </LoadingButton>
                        </Box>
                     )}
                  </Box>

                  {/* Section 3: Aadhaar Card Verification */}
                  <Box sx={{ mb: 4 }}>
                     <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        3. Aadhaar Card Verification
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Aadhaar Number"
                              {...getFieldProps('aadhar_number')}
                              error={Boolean(touched.aadhar_number && errors.aadhar_number)}
                              helperText={touched.aadhar_number && errors.aadhar_number}
                              disabled={values.aadhaar_verified && !!id}
                              InputProps={{
                                 endAdornment: values.aadhaar_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12}>
                           <Stack spacing={2}>
                              <Button
                                 component="label"
                                 variant="contained"
                                 startIcon={<CloudUploadIcon />}
                                 disabled={values.aadhaar_verified && !!id}
                              >
                                 Upload Aadhaar Card
                                 <VisuallyHiddenInput id='aadhar_upload' type="file" onChange={(e) => handleFiles(e)} />
                              </Button>
                              <Stack direction={'row'} spacing={1}>
                                 <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                                    {upload.aadhar_upload?.['name'] ? upload.aadhar_upload?.['name'] : "No File Chosen"}
                                 </Typography>
                                 {upload.aadhar_upload?.['name'] ? (
                                    <IconButton component={'a'} href={dataURItoBlob(upload.aadhar_upload?.['base64'])} target='_blank' rel="noreferrer">
                                       <LaunchIcon />
                                    </IconButton>
                                 ) : null}
                              </Stack>
                           </Stack>
                        </Grid>
                     </Grid>

                     {!id && (
                        <Box sx={{ mt: 3 }}>
                           <LoadingButton
                              variant="contained"
                              onClick={handleAadhaarVerification}
                              loading={kycLoading.aadhaar}
                              disabled={(!values.aadhar_number || !upload.aadhar_upload?.['name']) || values.aadhaar_verified}
                              startIcon={getStatusIcon(kycStatus.aadhaar)}
                              sx={{ mr: 2 }}
                           >
                              {kycLoading.aadhaar ? 'Verifying...' : values.aadhaar_verified ? 'Verified' : 'Verify Aadhaar'}
                           </LoadingButton>
                        </Box>
                     )}
                  </Box>

                  <Divider sx={{ my: 4 }} />

                  {/* Section 4: Bank Account Verification */}
                  <Box sx={{ mb: 4 }}>
                     <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        4. Bank Account Verification
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Bank Name"
                              {...getFieldProps('bank_name')}
                              error={Boolean(touched.bank_name && errors.bank_name)}
                              helperText={touched.bank_name && errors.bank_name}
                              disabled={values.bank_verified && !!id}
                              InputProps={{
                                 endAdornment: values.bank_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Account Number"
                              {...getFieldProps('bank_account_number')}
                              error={Boolean(touched.bank_account_number && errors.bank_account_number)}
                              helperText={touched.bank_account_number && errors.bank_account_number}
                              disabled={values.bank_verified && !!id}
                              InputProps={{
                                 endAdornment: values.bank_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="IFSC Code"
                              {...getFieldProps('bank_ifsc_code')}
                              error={Boolean(touched.bank_ifsc_code && errors.bank_ifsc_code)}
                              helperText={touched.bank_ifsc_code && errors.bank_ifsc_code}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              disabled={values.bank_verified && !!id}
                              InputProps={{
                                 endAdornment: values.bank_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField
                              fullWidth
                              label="Account Holder Name"
                              {...getFieldProps('bank_account_holder_name')}
                              error={Boolean(touched.bank_account_holder_name && errors.bank_account_holder_name)}
                              helperText={touched.bank_account_holder_name && errors.bank_account_holder_name}
                              disabled={values.bank_verified && !!id}
                              InputProps={{
                                 endAdornment: values.bank_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                        <Grid item xs={12}>
                           <TextField
                              fullWidth
                              label="Bank Address"
                              {...getFieldProps('bank_address')}
                              error={Boolean(touched.bank_address && errors.bank_address)}
                              helperText={touched.bank_address && errors.bank_address}
                              disabled={values.bank_verified && !!id}
                              InputProps={{
                                 endAdornment: values.bank_verified ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                 ) : null
                              }}
                           />
                        </Grid>
                     </Grid>

                     {!id && (
                        <Box sx={{ mt: 3 }}>
                           <LoadingButton
                              variant="contained"
                              onClick={handleBankVerification}
                              loading={kycLoading.bank}
                              disabled={(!values.bank_name || !values.bank_account_number || !values.bank_ifsc_code || !values.bank_account_holder_name) || values.bank_verified}
                              startIcon={getStatusIcon(kycStatus.bank)}
                              sx={{ mr: 2 }}
                           >
                              {kycLoading.bank ? 'Verifying...' : values.bank_verified ? 'Verified' : 'Verify Bank'}
                           </LoadingButton>
                        </Box>
                     )}
                  </Box>

                  <Divider sx={{ my: 4 }} />

                  {/* Section 5: Additional Details */}
                  <Box sx={{ mb: 4 }}>
                     <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        5. Additional Details
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12}>
                           <TextField fullWidth label="Personal Address" {...getFieldProps('address')} error={Boolean(touched.address && errors.address)} helperText={touched.address && errors.address} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="City" {...getFieldProps('city')} error={Boolean(touched.city && errors.city)} helperText={touched.city && errors.city} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="State" {...getFieldProps('state')} error={Boolean(touched.state && errors.state)} helperText={touched.state && errors.state} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="Country" {...getFieldProps('country')} error={Boolean(touched.country && errors.country)} helperText={touched.country && errors.country} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <TextField fullWidth label="Pincode" {...getFieldProps('pincode')} error={Boolean(touched.pincode && errors.pincode)} helperText={touched.pincode && errors.pincode} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                           <TextField fullWidth label="Nominee Name" {...getFieldProps('nominee_name')} error={Boolean(touched.nominee_name && errors.nominee_name)} helperText={touched.nominee_name && errors.nominee_name} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                           <TextField type='number' fullWidth label="Nominee Age" InputProps={{ inputProps: { min: 0, max: 100 } }} {...getFieldProps('nominee_age')} error={Boolean(touched.nominee_age && errors.nominee_age)} helperText={touched.nominee_age && errors.nominee_age} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                           <TextField fullWidth label="Nominee Relation" {...getFieldProps('nominee_relation')} error={Boolean(touched.nominee_relation && errors.nominee_relation)} helperText={touched.nominee_relation && errors.nominee_relation} />
                        </Grid>
                     </Grid>

                     {/* KYC Status Summary - Only for create mode */}
                     {!id && (
                        <Box sx={{ mt: 4 }}>
                           <Typography variant="h6" gutterBottom>
                              KYC Verification Status
                           </Typography>
                           <Stack direction="row" spacing={2} flexWrap="wrap">
                              <Chip
                                 label={`PAN: ${values.pan_verified ? 'VERIFIED' : 'PENDING'}`}
                                 color={values.pan_verified ? 'success' : 'warning'}
                                 icon={getStatusIcon(values.pan_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                              />
                              <Chip
                                 label={`Aadhaar: ${values.aadhaar_verified ? 'VERIFIED' : 'PENDING'}`}
                                 color={values.aadhaar_verified ? 'success' : 'warning'}
                                 icon={getStatusIcon(values.aadhaar_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                              />
                              <Chip
                                 label={`Bank: ${values.bank_verified ? 'VERIFIED' : 'PENDING'}`}
                                 color={values.bank_verified ? 'success' : 'warning'}
                                 icon={getStatusIcon(values.bank_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                              />
                           </Stack>
                        </Box>
                     )}
                  </Box>
               </CardContent>
               <Stack ml={'20px'} spacing={1} direction={'row'}>
                  <FormControlLabel
                     value="end"
                     control={<Checkbox checked={checked}
                        onChange={handleChangeCheckTerms} />}
                     label=""
                     labelPlacement="end"
                  />
                  <Typography align='center' justifyContent={'center'} display={'flex'} alignItems={'center'}>I Accept the <a href={require('../../../img/terms and conditions.pdf')} target="_blank" rel="noreferrer" style={{ cursor: 'pointer', marginLeft: '5px' }}>Terms and Conditions</a></Typography>
               </Stack>
               <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button href="#/dashboard/adminProcurementUser" variant='contained' sx={{ backgroundColor: '#3f51b5', '&:hover': { color: '#ffff', backgroundColor: '#3f51b5' } }}>Back</Button>
                  {console.log(!checked , !isEmailVerified , !isPhoneVerified , !values.pan_verified , !values.aadhaar_verified , !values.bank_verified,"checked")}
                  <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}
                   disabled={false} sx={{ backgroundColor: '#3f51b5', '&:hover': { color: '#ffff', backgroundColor: '#3f51b5' } }}>Save </LoadingButton>
               </CardActions>
            </Card>

            {/* Verification Modal */}
            <Modal open={verificationModalOpen}>
               <Box
                  sx={{
                     position: 'absolute',
                     top: '50%',
                     left: '50%',
                     transform: 'translate(-50%, -50%)',
                     width: 400,
                     backgroundColor: 'white',
                     borderRadius: 2,
                     boxShadow: 24,
                     p: 4,
                     textAlign: 'center'
                  }}
               >
                  <CircularProgress 
                     size={64} 
                     sx={{ 
                        color: 'primary.main', 
                        mb: 2 
                     }} 
                  />
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                     Verifying {verificationType}...
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                     Please wait while we verify your {verificationType.toLowerCase()} information. This may take a few moments.
                  </Typography>
                  <Box sx={{ 
                     display: 'flex', 
                     justifyContent: 'center', 
                     alignItems: 'center',
                     gap: 1,
                     mt: 2
                  }}>
                     <CircularProgress size={20} />
                     <Typography variant="body2" color="text.secondary">
                        Processing...
                     </Typography>
                  </Box>
               </Box>
            </Modal>

            {/* OTP Verification Modal */}
            <Modal open={otpModalOpen} onClose={handleOtpClose}>
               <Box
                  sx={{
                     position: 'absolute',
                     top: '50%',
                     left: '50%',
                     transform: 'translate(-50%, -50%)',
                     width: 400,
                     bgcolor: 'background.paper',
                     borderRadius: 2,
                     boxShadow: 24,
                     p: 4,
                  }}
               >
                  <Typography variant="h6" align="center" gutterBottom>
                     Enter OTP for {otpType === "email" ? "Email" : "Phone"}
                  </Typography>
                  <TextField
                     fullWidth
                     label="OTP"
                     type="text"
                     value={otpValue}
                     onChange={(e) => setOtpValue(e.target.value)}
                     error={Boolean(!otpValue)}
                     helperText={!otpValue ? "OTP is required" : ""}
                     sx={{ mb: 2, mt: 2 }}
                  />
                  <Stack direction="row" spacing={2} justifyContent="center">
                     <Button
                        variant="outlined"
                        onClick={handleOtpClose}
                        sx={{ minWidth: 100 }}
                     >
                        Cancel
                     </Button>
                     <Button
                        variant="contained"
                        onClick={handleOtpSubmit}
                        disabled={!otpValue}
                        sx={{ minWidth: 100 }}
                     >
                        Verify OTP
                     </Button>
                  </Stack>
               </Box>
            </Modal>
         </Form>
      </FormikProvider>
   )
};

export default AdminProcurementCreate;

const VisuallyHiddenInput = styled('input')({
   clip: 'rect(0 0 0 0)',
   clipPath: 'inset(50%)',
   height: 1,
   overflow: 'hidden',
   position: 'absolute',
   bottom: 0,
   left: 0,
   whiteSpace: 'nowrap',
   width: 1,
});

